import { PublicClientApplication } from '@azure/msal-browser';
import { HomeOutlined, Logout, PersonAddAlt1 } from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';

import { useAuth } from 'hooks/useAuth';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listarMinhasListas } from 'services/adminListas';
import api from 'services/api';
import { trackEvent } from 'utils/tracking';

const microsoftClientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const microsoftAuthority =
  process.env.REACT_APP_MICROSOFT_AUTHORITY || 'https://login.microsoftonline.com/common';

const msalInstance =
  typeof window !== 'undefined' && microsoftClientId
    ? new PublicClientApplication({
        auth: {
          clientId: microsoftClientId,
          authority: microsoftAuthority,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      })
    : null;

const DEFAULT_HEADER_BACKGROUND_IMAGE =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 180 180%27%3E%3Ccircle cx=%2722%27 cy=%2726%27 r=%2710%27 fill=%27rgba(255,255,255,0.20)%27/%3E%3Ccircle cx=%2788%27 cy=%2748%27 r=%2712%27 fill=%27rgba(255,255,255,0.15)%27/%3E%3Ccircle cx=%27142%27 cy=%2730%27 r=%278%27 fill=%27rgba(255,255,255,0.18)%27/%3E%3Ccircle cx=%2748%27 cy=%27112%27 r=%279%27 fill=%27rgba(255,255,255,0.16)%27/%3E%3Ccircle cx=%27120%27 cy=%27124%27 r=%2714%27 fill=%27rgba(255,255,255,0.14)%27/%3E%3Ccircle cx=%27156%27 cy=%27148%27 r=%2710%27 fill=%27rgba(255,255,255,0.18)%27/%3E%3C/svg%3E"), radial-gradient(circle at 14% 18%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 40%), radial-gradient(circle at 84% 26%, rgba(248,235,220,0.45) 0%, rgba(248,235,220,0) 34%), linear-gradient(100deg, #88adc0 0%, #a6c4d0 42%, #c7d9db 100%)';

const Header: React.FC = () => {
  const authButtonWidth = 260;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    user,
    logout,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithMagicToken,
    loginWithOtp,
  } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const isGuestUser = user?.authProvider === 'guest';

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [msalInitError, setMsalInitError] = useState<string>('');
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string>('');
  const [otpCode, setOtpCode] = useState('');
  const [devMagicToken, setDevMagicToken] = useState<string>('');
  const [devOtpCode, setDevOtpCode] = useState<string>('');
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [magicSentDialogOpen, setMagicSentDialogOpen] = useState(false);

  const isValidEmail = useCallback((value: string) => {
    return /.+@.+\..+/.test(value);
  }, []);

  const openUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeUserMenu = () => {
    setAnchorEl(null);
  };

  const clearLoginQueryFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('login')) {
      params.delete('login');
    }
    if (params.has('magicToken')) {
      params.delete('magicToken');
    }
    navigate(
      { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const openLoginDialog = useCallback(
    (reason: string = 'manual') => {
      setLoginError('');
      setInfoMessage('');
      setDevMagicToken('');
      setDevOtpCode('');
      setOtpCode('');
      setEmailInput('');
      setEmailError('');
      setLoginDialogOpen(true);
      trackEvent('login_dialog_open', {
        reason,
        isAuthenticated,
        isGuestUser,
        path: location.pathname,
      });
    },
    [isAuthenticated, isGuestUser, location.pathname]
  );

  const handleCloseLoginDialog = useCallback(() => {
    setLoginDialogOpen(false);
    clearLoginQueryFromUrl();
  }, [clearLoginQueryFromUrl]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!isAuthenticated && params.get('login') === '1') {
      openLoginDialog('query_param');
    }

    if (isAuthenticated && params.has('login')) {
      clearLoginQueryFromUrl();
    }
  }, [clearLoginQueryFromUrl, isAuthenticated, location.search, openLoginDialog]);

  // Se já estiver autenticado e na rota /login, envia direto para Minha lista
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/login') {
      navigate('/admin/listas', { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  const goToMinhaLista = async () => {
    closeUserMenu();
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    try {
      const listas = await listarMinhasListas();
      const slug = listas[0]?.slug;
      navigate(slug ? `/${slug}` : '/criar-lista');
    } catch {
      navigate('/');
    }
  };

  const handleLogout = () => {
    trackEvent('logout_click', {
      isGuestUser,
      path: location.pathname,
    });
    logout();
    navigate('/');
  };

  const handleSwitchUser = () => {
    trackEvent('switch_user_click', {
      path: location.pathname,
    });
    logout();
    sessionStorage.setItem('postLoginRedirect', '/admin/listas');
    openLoginDialog('switch_user');
    closeUserMenu();
  };

  const handleLinkEmail = () => {
    trackEvent('guest_link_email_click', {
      path: location.pathname,
    });
    sessionStorage.setItem('postLoginRedirect', '/admin/listas');
    openLoginDialog('link_email');
  };

  const handleMenuNavigate = (path: string) => {
    trackEvent('header_menu_navigate', {
      path,
      sourcePath: location.pathname,
      isAuthenticated,
    });
    navigate(path);
    closeUserMenu();
  };

  const handleOpenReceivedGifts = async () => {
    trackEvent('header_menu_open_received_gifts', {
      sourcePath: location.pathname,
      isAuthenticated,
    });

    try {
      const listas = await listarMinhasListas();
      const listaId = listas[0]?.id;
      if (listaId) {
        navigate(`/admin/listas/${listaId}/recebidos`);
      } else {
        navigate('/criar-lista');
      }
    } catch {
      navigate('/admin/listas');
    } finally {
      closeUserMenu();
    }
  };

  const handleLoginSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setLoginError('Não foi possível obter as credenciais do Google. Tente novamente.');
      return;
    }

    try {
      setLoginLoading(true);
      setLoginError('');
      await loginWithGoogle(credentialResponse.credential);
      trackEvent('login_google_success', {
        source: 'header_dialog',
      });
      const redirect = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect');
      handleCloseLoginDialog();
      if (redirect) {
        navigate(redirect, { replace: true });
      } else {
        navigate('/admin/listas', { replace: true });
      }
    } catch (error) {
      console.error('Erro no login com Google', error);
      trackEvent('login_google_error', {
        source: 'header_dialog',
      });
      setLoginError('Falha ao autenticar com o Google. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLoginError = () => {
    trackEvent('login_google_error', {
      source: 'google_component',
    });
    setLoginError('Falha no processo de autenticação do Google. Tente novamente.');
  };

  const handleSendMagicLink = useCallback(async () => {
    if (!emailInput || emailError) {
      setLoginError('Informe um email válido para enviar o link.');
      return;
    }
    try {
      setLoginLoading(true);
      setLoginError('');
      setInfoMessage('');
      setDevMagicToken('');
      const response = await api.post('/auth/magic/request', { email: emailInput });
      trackEvent('magic_link_request_sent', {
        source: 'header_dialog',
      });
      setInfoMessage(
        'Se o email existir, enviaremos um link de acesso. Verifique sua caixa de entrada.'
      );
      if (response.data?.devToken) {
        setDevMagicToken(response.data.devToken);
      }
      if (response.data?.devLink) {
        setDevMagicToken(response.data.devLink);
      }
      setMagicSentDialogOpen(true);
      setLoginDialogOpen(false);
    } catch (error: any) {
      console.error('Erro ao solicitar magic link', error);
      trackEvent('magic_link_request_error', {
        source: 'header_dialog',
        status: error?.response?.status,
      });
      if (error?.response?.status === 429) {
        setLoginError('Aguarde alguns segundos antes de pedir outro link.');
      } else {
        setLoginError('Falha ao solicitar link mágico. Tente novamente.');
      }
    } finally {
      setLoginLoading(false);
    }
  }, [emailInput, emailError]);

  const handleSendOtp = useCallback(async () => {
    if (!emailInput || emailError) {
      setLoginError('Informe um email válido para enviar o código.');
      return;
    }
    try {
      setLoginLoading(true);
      setLoginError('');
      setInfoMessage('');
      setDevOtpCode('');
      const response = await api.post('/auth/otp/request', { email: emailInput });
      trackEvent('otp_request_sent', {
        source: 'header_dialog',
      });
      setInfoMessage('Se o email existir, enviaremos um código de acesso.');
      if (response.data?.devCode) {
        setDevOtpCode(response.data.devCode);
      }
      setOtpDialogOpen(true);
      setLoginDialogOpen(false);
    } catch (error: any) {
      console.error('Erro ao solicitar OTP', error);
      trackEvent('otp_request_error', {
        source: 'header_dialog',
        status: error?.response?.status,
      });
      if (error?.response?.status === 429) {
        setLoginError('Aguarde alguns segundos antes de pedir outro código.');
      } else {
        setLoginError('Falha ao solicitar código. Tente novamente.');
      }
    } finally {
      setLoginLoading(false);
    }
  }, [emailInput, emailError]);

  const handleVerifyOtp = useCallback(async () => {
    if (!emailInput || !otpCode) {
      setLoginError('Informe email e código para entrar.');
      return;
    }
    try {
      setLoginLoading(true);
      setLoginError('');
      setInfoMessage('');
      await loginWithOtp(emailInput, otpCode);
      trackEvent('otp_verify_success', {
        source: 'header_dialog',
      });
      const redirect = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect');
      setOtpDialogOpen(false);
      handleCloseLoginDialog();
      if (redirect) {
        navigate(redirect, { replace: true });
      } else {
        navigate('/admin/listas', { replace: true });
      }
    } catch (error) {
      console.error('Erro ao validar OTP', error);
      trackEvent('otp_verify_error', {
        source: 'header_dialog',
      });
      setLoginError('Código inválido ou expirado.');
    } finally {
      setLoginLoading(false);
    }
  }, [emailInput, handleCloseLoginDialog, loginWithOtp, navigate, otpCode]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const magicToken = params.get('magicToken');
    if (magicToken) {
      const doLogin = async () => {
        try {
          setLoginLoading(true);
          setLoginError('');
          await loginWithMagicToken(magicToken);
          trackEvent('magic_link_verify_success', {
            source: 'url_param',
          });
          const redirect = sessionStorage.getItem('postLoginRedirect');
          sessionStorage.removeItem('postLoginRedirect');
          if (redirect) {
            navigate(redirect, { replace: true });
          } else {
            navigate('/admin/listas', { replace: true });
          }
        } catch (error) {
          console.error('Erro ao validar magic token via URL', error);
          trackEvent('magic_link_verify_error', {
            source: 'url_param',
          });
          setLoginDialogOpen(true);
          setLoginError('Link de acesso inválido ou expirado. Peça um novo link.');
        } finally {
          setLoginLoading(false);
          clearLoginQueryFromUrl();
        }
      };
      doLogin();
    }
  }, [clearLoginQueryFromUrl, location.search, loginWithMagicToken, navigate]);

  const handleMicrosoftLogin = useCallback(async () => {
    if (!msalInstance) {
      setLoginError('Configuração da Microsoft não está disponível.');
      return;
    }
    try {
      setLoginLoading(true);
      setLoginError('');
      setMsalInitError('');
      await msalInstance.initialize();
      const result = await msalInstance.loginPopup({ scopes: ['openid', 'email', 'profile'] });
      const idToken = result.idToken;
      if (!idToken) {
        throw new Error('Token da Microsoft ausente');
      }
      await loginWithMicrosoft(idToken);
      trackEvent('login_microsoft_success', {
        source: 'header_dialog',
      });
      const redirect = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect');
      handleCloseLoginDialog();
      if (redirect) {
        navigate(redirect, { replace: true });
      } else {
        navigate('/admin/listas', { replace: true });
      }
    } catch (error) {
      console.error('Erro no login com Microsoft', error);
      trackEvent('login_microsoft_error', {
        source: 'header_dialog',
      });
      setLoginError('Falha ao autenticar com Microsoft. Tente novamente.');
      setMsalInitError('Falha ao inicializar o MSAL.');
    } finally {
      setLoginLoading(false);
    }
  }, [handleCloseLoginDialog, loginWithMicrosoft, navigate]);

  const loginDialogTitle = useMemo(
    () => (isAuthenticated ? 'Trocar usuário' : 'Fazer login'),
    [isAuthenticated]
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        '--app-header-default-image': DEFAULT_HEADER_BACKGROUND_IMAGE,
        backgroundColor: 'var(--app-header-bg, #95b9c9)',
        backgroundImage: 'var(--app-header-bg-image, var(--app-header-default-image))',
        backgroundSize: '180px 180px, auto, auto, auto',
        borderBottom: '1px solid rgba(255,255,255,0.35)',
      }}
    >
      <Toolbar
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <IconButton
          color="inherit"
          onClick={() => {
            closeUserMenu();
            navigate('/');
          }}
          size="small"
          aria-label="Início"
        >
          <HomeOutlined />
        </IconButton>

        {isAuthenticated ? (
          <Box display="flex" alignItems="center" gap={1}>
            {isAdmin && (
              <Button color="inherit" onClick={() => navigate('/admin/produtos-afiliados')}>
                Catálogo afiliado
              </Button>
            )}
            <Tooltip title={user?.email ?? 'Minha conta'}>
              <IconButton color="inherit" onClick={openUserMenu} size="small">
                <Avatar src={user?.fotoUrl ?? undefined} sx={{ width: 32, height: 32 }}>
                  {user?.nome?.[0]}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeUserMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box px={2} py={1.5} display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="subtitle2">{user?.nome}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {isGuestUser ? 'Sessão no navegador (convidado)' : user?.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={() => void goToMinhaLista()}>Minha lista</MenuItem>
              <MenuItem onClick={() => void handleOpenReceivedGifts()}>
                Presentes recebidos
              </MenuItem>
              {isGuestUser && <MenuItem onClick={handleLinkEmail}>Vincular ao e-mail</MenuItem>}
              {isAdmin && (
                <MenuItem onClick={() => handleMenuNavigate('/admin/produtos-afiliados')}>
                  Catálogo afiliado
                </MenuItem>
              )}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleSwitchUser}>
                <PersonAddAlt1 fontSize="small" sx={{ mr: 1 }} /> Trocar usuário
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeUserMenu();
                  handleLogout();
                }}
              >
                <Logout fontSize="small" sx={{ mr: 1 }} /> Sair
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Button
            color="inherit"
            onClick={() => {
              trackEvent('login_cta_click', {
                source: 'header',
                path: location.pathname,
              });
              sessionStorage.setItem('postLoginRedirect', '/admin/listas');
              openLoginDialog('login_cta_header');
            }}
          >
            Fazer login
          </Button>
        )}
      </Toolbar>

      <Dialog open={loginDialogOpen} onClose={handleCloseLoginDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{loginDialogTitle}</DialogTitle>
        <DialogContent>
          {loginError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loginError}
            </Alert>
          )}
          {infoMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {infoMessage}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2} my={1}>
            {loginLoading ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={32} />
              </Box>
            ) : (
              <>
                <Box display="flex" flexDirection="column" justifyContent="center" gap={1}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    width={'100%'}
                    alignItems="center"
                    marginX={'auto'}
                    sx={{ width: authButtonWidth }}
                  >
                    <Card
                      elevation={2}
                      data-testid="google-login-card"
                      sx={{
                        width: authButtonWidth,
                        ' svg:first-of-type': { position: 'relative', left: '20px' },
                      }}
                    >
                      <GoogleLogin
                        onSuccess={handleLoginSuccess}
                        onError={handleLoginError}
                        useOneTap={false}
                      />
                    </Card>
                  </Box>
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={handleMicrosoftLogin}
                    disabled={!msalInstance}
                    sx={{
                      width: authButtonWidth,
                      alignSelf: 'center',
                      backgroundColor: '#ffffff',
                      color: '#5e5e5e',
                      border: '1px solid #d6d6d6',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#f3f3f3',
                        borderColor: '#c0c0c0',
                      },
                    }}
                    startIcon={
                      <Box
                        component="img"
                        src="/microsoft-icon.svg"
                        alt="Microsoft"
                        sx={{ width: 20, height: 20 }}
                      />
                    }
                  >
                    Entrar com Microsoft
                  </Button>
                </Box>

                <Divider>ou entre com email</Divider>

                <TextField
                  label="Seu email"
                  type="email"
                  fullWidth
                  value={emailInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmailInput(value);
                    setEmailError(value && !isValidEmail(value) ? 'Email inválido' : '');
                  }}
                  error={!!emailError}
                  helperText={emailError}
                />
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSendMagicLink}
                    disabled={!emailInput || !!emailError}
                  >
                    Link Mágico
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleSendOtp}
                    disabled={!emailInput || !!emailError}
                  >
                    Código (OTP)
                  </Button>
                </Box>
              </>
            )}
          </Box>
          {!msalInstance && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Configure REACT_APP_MICROSOFT_CLIENT_ID (frontend) e MICROSOFT_CLIENT_ID (backend)
              para habilitar o login com Microsoft.
            </Alert>
          )}
          {msalInitError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {msalInitError}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Seus convidados não precisam de login.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Digite o código</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enviamos um código para {emailInput}. Digite abaixo para entrar.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Código"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              fullWidth
              autoFocus
            />
            <Button variant="contained" onClick={handleVerifyOtp} disabled={loginLoading}>
              Validar e entrar
            </Button>
            {devOtpCode && <Alert severity="warning">Código dev: {devOtpCode}</Alert>}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={magicSentDialogOpen}
        onClose={() => setMagicSentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Link enviado</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Se o email existir, você receberá um link para entrar. Verifique também a caixa de spam.
          </Typography>
          {devMagicToken && (
            <Alert severity="warning">Token/Link de desenvolvimento: {devMagicToken}</Alert>
          )}
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => setMagicSentDialogOpen(false)}>
            Fechar
          </Button>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Header;
